'use client';

/**
 * Presentational pieces for the quiz catalog page.
 *
 * Module: app/quizzes/quiz-grid
 * Layer:  component
 *
 * Purpose:
 *     Houses the toolbar (search + course filter), the results grid, and the
 *     loading / error / empty states for the quiz catalog. Splitting these out
 *     of page.tsx keeps each function under the CC<=5 / <=60-line gates and
 *     mirrors the courses/library catalog layout.
 *
 * AI context:
 *     - Entry points called by: src/app/quizzes/page.tsx.
 *     - Depends on: components/quizzes/QuizCard, use-quizzes controller, i18n.
 *     - Side effects: none; presentational. Input changes delegate to the
 *       controller passed in via props.
 *     - Invariants: every visible string comes from the i18n catalogue.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - No untrusted input beyond the already-validated quiz summaries.
 */

import { type ChangeEvent, type SubmitEvent, useCallback } from 'react';
import type React from 'react';
import { QuizCard } from '@/components/quizzes/QuizCard';
import { useTranslation } from '@/lib/i18n';
import type { QuizSummary } from '@/lib/quizzes';
import type { CourseOption, QuizzesController } from './use-quizzes';

/** Centered loading panel shown before the first payload arrives. */
export function LoadingPanel(): React.JSX.Element {
  const { t } = useTranslation();
  return <p className='py-12 text-center opacity-70'>{t('quizzes.loading')}</p>;
}

/** Error panel with a retry button. */
export function ErrorPanel(props: { readonly onRetry: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='py-12 text-center' role='alert'>
      <p className='mb-4 opacity-70'>{t('quizzes.failedToLoad')}</p>
      <button
        type='button'
        onClick={props.onRetry}
        className='rounded-lg border border-border px-4 py-2 text-sm'
      >
        {t('quizzes.retry')}
      </button>
    </div>
  );
}

/** Empty-state panel with a clear-filters action. */
function EmptyResults(props: { readonly onClear: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='rounded-2xl border border-border py-12 text-center'>
      <h3 className='mb-2 text-lg font-semibold'>{t('quizzes.empty.title')}</h3>
      <p className='mb-4 opacity-70'>{t('quizzes.empty.subtitle')}</p>
      <button
        type='button'
        onClick={props.onClear}
        className='rounded-lg border border-border px-4 py-2 text-sm'
      >
        {t('quizzes.empty.clear')}
      </button>
    </div>
  );
}

/** Course filter dropdown driven by the loaded course options. */
function CourseFilter(props: {
  readonly options: readonly CourseOption[];
  readonly value: number | null;
  readonly onSelect: (id: number | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { options, value, onSelect } = props;
  const handle = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      const raw = e.target.value;
      onSelect(raw === '' ? null : Number(raw));
    },
    [onSelect],
  );
  return (
    <select
      value={value === null ? '' : String(value)}
      onChange={handle}
      aria-label={t('quizzes.filter.courseLabel')}
      className='rounded-xl border border-border bg-background px-4 py-3'
    >
      <option value=''>{t('quizzes.filter.allCourses')}</option>
      {options.map((opt) => (
        <option key={opt.id} value={String(opt.id)}>
          {opt.title}
        </option>
      ))}
    </select>
  );
}

/** Search box + course filter toolbar. */
export function Toolbar(props: { readonly ctrl: QuizzesController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onSubmit = useCallback(
    (e: SubmitEvent<HTMLFormElement>): void => {
      e.preventDefault();
      ctrl.submitSearch();
    },
    [ctrl],
  );
  const onQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => ctrl.setDraftQuery(e.target.value),
    [ctrl],
  );
  return (
    <div className='mb-8 flex flex-col gap-4 md:flex-row'>
      <form onSubmit={onSubmit} className='flex flex-1 gap-2'>
        <input
          type='text'
          value={ctrl.draftQuery}
          onChange={onQueryChange}
          placeholder={t('quizzes.searchPlaceholder')}
          className='w-full rounded-xl border border-border px-4 py-3'
        />
        <button type='submit' className='rounded-xl border border-border px-4 py-3 text-sm'>
          {t('quizzes.search')}
        </button>
      </form>
      <CourseFilter
        options={ctrl.courseOptions}
        value={ctrl.courseId}
        onSelect={ctrl.selectCourse}
      />
    </div>
  );
}

/** Grid of quiz cards. */
function QuizGrid(props: { readonly quizzes: readonly QuizSummary[] }): React.JSX.Element {
  return (
    <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
      {props.quizzes.map((quiz) => (
        <QuizCard key={quiz.id} quiz={quiz} />
      ))}
    </div>
  );
}

/** Results region: empty state or the card grid. */
export function Results(props: { readonly ctrl: QuizzesController }): React.JSX.Element {
  const { ctrl } = props;
  if (ctrl.quizzes.length === 0) {
    return <EmptyResults onClear={ctrl.clearFilters} />;
  }
  return <QuizGrid quizzes={ctrl.quizzes} />;
}
